import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdBanner from '../components/AdBanner';
import VideoPlayer from '../components/VideoPlayer';
import { usePageSEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "Drive Australia Wide with Tony Tardio" promo landing page.
// English-only by design (owner ruling 2026-08-10) — served at both /drive and
// /en/drive so either URL works. Content mirrors the Scaletta weekly template
// for the Drive Show (fc8e492d…); update SHOW_GRID if the run-sheet changes.

// Contact points for the on-air audience. The mobile takes both SMS and
// WhatsApp; the card hides itself if the number is ever blanked out.
const PHONE_NUMBER = '1300 363 900';
const SMS_NUMBER = '+61 493 383 144';
const TONY_EMAIL = 'tony@reteitalia.com.au';
const digits = (n) => n.replace(/[^\d]/g, '');        // wa.me wants bare digits
const dial = (n) => n.replace(/[^\d+]/g, '');         // tel:/sms: keep the +61

// Artwork. Missing files hide themselves (onError) so the page never shows a
// broken image: drop the files into frontend/public/ to switch them on.
const HERO_ART = '/drive-australia-wide.jpg';
const UNLEASHED_LOGO = '/tardio-unleashed.png';

// Rete Italia apps (same listings the Home and Listen pages use).
const APP_IOS = 'https://apps.apple.com/au/app/rete-italia/id1445988538';
const APP_ANDROID =
  'https://play.google.com/store/apps/details?id=com.xdevel.reteitaliaaustralia2&hl=en_AU';

// Where the show can be WATCHED live. Blank entries render the card without a
// link, so the section is safe to ship before a destination is confirmed.
const WATCH_YOUTUBE = 'https://www.youtube.com/@TardioUnleashed/streams';
const WATCH_ILGLOBO_TV = 'https://play.ilglobo.com';
const WATCH_SITE = '#watch';     // the live player embedded further down this page

// Tony's own channels. Blank = button hidden.
const TONY_X = '';
const TONY_YOUTUBE = 'https://www.youtube.com/@TardioUnleashed';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

// Fallback lineup, used only if the live Scaletta feed can't be reached.
// The real grid comes from /api/show-lineup, so producers edit it in
// /admin/scaletta and this page follows — no code change needed.
// [time, Mon, Tue, Wed, Thu, Fri] — '' = no segment in that slot
const SHOW_GRID = [
  ['4:00', 'Editorial', 'Editorial', 'The Editorial', 'The Editorial', 'The Editorial'],
  ['4:05', 'News Blueprint', 'News: VIC, NSW, QLD, WA', 'News', 'News Blueprint', 'News Blueprint'],
  ['4:10', '', 'National Politics & International', '', '', ''],
  ['4:15', 'Story of the Day', 'Interview with the newsmaker of the day', 'Story of the Day', 'Story of the Day', 'Story of the Day'],
  ['4:30', 'Federal Politics', '', 'State Politics (VIC/NSW focus)', 'Federal Round-Table', 'The Political Week in Review'],
  ['4:45', 'State Politics (VIC/NSW focus)', '', 'Made in Italy', 'Italian Club Land', ''],
  ['5:00', 'Monday Feature Interview', 'Australians Living in Italy', 'Global Italy: Greater Europe', 'Sindaco of the Week', 'Friday Night Aperitivo'],
  ['5:15', 'Italian Restaurant of the Week', 'Italian Travel', 'Holiday Resorts in Italy', 'Italian Australian Chef', 'Weekend Travel & Culture'],
  ['5:30', 'LIVE CROSS: ROME', 'LIVE CROSS: MILAN', 'LIVE CROSS: VENICE', 'LIVE CROSS: CALABRIA & SICILY', 'LIVE CROSS: FLORENCE'],
  ['5:45', 'Real Estate: Sydney & Melbourne', 'Psychology and Health', 'THE LAW', 'Finance & the Share Market', 'Italian Hotel of the Week'],
];

const HIGHLIGHTS = [
  {
    icon: '📰',
    title: 'The news, properly explained',
    text: 'Tony opens every day with his editorial, followed by a national news wrap across Victoria, NSW, Queensland and WA.',
  },
  {
    icon: '🏛️',
    title: 'Politics that matters to our community',
    text: 'Federal politics on Monday, state politics midweek, the Federal Round-Table on Thursday and the Political Week in Review on Friday.',
  },
  {
    icon: '🇮🇹',
    title: 'A live cross to Italy — every single day',
    text: 'Monday Rome, Tuesday Milan, Wednesday Venice, Thursday Calabria & Sicily, Friday Florence. Local voices and local news, straight from home.',
  },
  {
    icon: '🍝',
    title: 'The best of Italian life, here and there',
    text: 'Made in Italy, Italian Club Land, the Sindaco of the Week, Italian Australian chefs, restaurants, travel and holiday resorts.',
  },
  {
    icon: '💼',
    title: 'Experts you can actually use',
    text: 'Real estate, finance and the share market, health and psychology, and THE LAW — every week, with people who know their field.',
  },
  {
    icon: '🥂',
    title: 'Friday Night Aperitivo',
    text: 'The week signed off the way it should be — with good conversation and better company.',
  },
];

const hideOnError = (e) => { e.currentTarget.style.display = 'none'; };

const Drive = () => {
  // Live lineup from the Scaletta run-sheet; falls back to SHOW_GRID.
  const [grid, setGrid] = useState(SHOW_GRID);
  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/show-lineup`, { params: { show: 'drive' } })
      .then((r) => {
        const rows = r?.data?.rows;
        if (alive && Array.isArray(rows) && rows.length) {
          setGrid(rows.map((row) => [row.time, ...row.cells]));
        }
      })
      .catch(() => {});   // keep the built-in lineup
    return () => { alive = false; };
  }, []);

  usePageSEO({
    title: 'Drive Australia Wide with Tony Tardio — Weekdays 4pm–6pm | Rete Italia',
    description:
      'Drive Australia Wide with Tony Tardio on Rete Italia. News, politics, culture and a live cross to Italy every day. Monday to Friday, 4pm to 6pm, Australia wide.',
    path: '/drive',
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-16">
      <div className="container mx-auto px-4">
        <AdBanner className="mb-8" />

        {/* ── Hero ─────────────────────────────────────────────────────────
            The artwork already carries the title, times and branding, so it is
            shown as the image itself rather than as a washed-out backdrop with
            duplicate text over it. The caption block below doubles as the
            fallback if the artwork ever fails to load. */}
        <div className="rounded-3xl overflow-hidden shadow-2xl mb-12 bg-gradient-to-br from-[#0b1c33] via-[#12294a] to-[#00247D]">
          <img
            src={HERO_ART}
            alt="Drive Australia Wide with Tony Tardio — every day, Monday to Friday, 4pm to 6pm on Rete Italia"
            onError={hideOnError}
            className="block w-full h-auto"
          />
          <div className="p-6 md:p-10 text-white text-center">
            <h1 className="text-2xl md:text-4xl font-extrabold leading-tight">
              Drive Australia Wide <span className="text-white/80">with</span> Tony Tardio
            </h1>
            <p className="mt-2 text-lg md:text-xl font-semibold text-[#ff8a94]">
              Monday to Friday · 4pm – 6pm
            </p>
            <p className="mt-3 max-w-2xl mx-auto text-white/85 text-base md:text-lg">
              Two hours that connect Italian Australia — from Canberra to Rome,
              every weekday afternoon.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <a
                href="/ascoltaci"
                className="bg-[#CE2B37] hover:bg-[#b0242e] transition-colors px-6 py-3 rounded-xl font-bold shadow-lg"
              >
                ▶ Listen live
              </a>
              <a
                href={`tel:${dial(PHONE_NUMBER)}`}
                className="bg-white/15 hover:bg-white/25 backdrop-blur transition-colors px-6 py-3 rounded-xl font-bold border border-white/30"
              >
                📞 Call {PHONE_NUMBER}
              </a>
            </div>
          </div>
        </div>

        {/* ── Intro ────────────────────────────────────────────────────── */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
            Every weekday at 4pm, <strong>Tony Tardio</strong> takes the wheel for two hours of
            news, politics, culture and conversation for Italian Australians — broadcast
            <strong> Australia wide</strong> on Rete Italia.
          </p>
          <p className="mt-4 text-lg text-gray-600 leading-relaxed">
            It's the drive home with substance: the day's biggest story unpacked with the people
            who made it, federal and state politics without the spin, and a live cross to Italy
            every single afternoon.
          </p>
        </div>

        {/* ── Watch live (video simulcast) ─────────────────────────────── */}
        <div
          id="watch"
          className="relative rounded-3xl p-8 md:p-12 mb-14 shadow-2xl overflow-hidden bg-gradient-to-br from-[#CE2B37] via-[#a81f2b] to-[#0b1c33] text-white scroll-mt-24"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-20 -mt-20" />
          <div className="relative">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="flex h-3 w-3 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
              </span>
              <span className="text-xs font-extrabold tracking-[0.2em] uppercase">Live on video</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-center leading-tight">
              Don't just listen — <span className="underline decoration-4 underline-offset-4">watch</span> the show
            </h2>
            <p className="text-center text-white/90 text-lg md:text-xl mt-4 max-w-3xl mx-auto">
              Drive is streamed <strong>live on video</strong>, every weekday from 4pm. See Tony in
              the studio, watch the interviews as they happen and catch every live cross to Italy —
              on three screens, wherever you are.
            </p>

            {/* The live simulcast, playing right here */}
            <div className="mt-8 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/25 bg-black">
              <VideoPlayer />
            </div>
            <p className="text-center text-white/75 text-sm mt-3">
              Live video, weekdays 4pm – 6pm. Outside those hours you'll see whatever is
              currently on Rete Italia TV.
            </p>

            <div className="grid sm:grid-cols-3 gap-5 mt-9">
              <a
                href={WATCH_SITE}
                className="bg-white/15 hover:bg-white/25 backdrop-blur transition-colors rounded-2xl p-6 text-center border border-white/25 flex flex-col"
              >
                <div className="text-4xl mb-2">💻</div>
                <div className="font-bold text-lg">Here on reteitalia.com.au</div>
                <div className="text-white/80 text-sm mt-1 flex-1">The player above — no app, no sign-in</div>
                <div className="mt-3 text-sm font-bold underline">Watch now →</div>
              </a>

              {WATCH_ILGLOBO_TV ? (
                <a
                  href={WATCH_ILGLOBO_TV}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/15 hover:bg-white/25 backdrop-blur transition-colors rounded-2xl p-6 text-center border border-white/25 flex flex-col"
                >
                  <div className="text-4xl mb-2">📺</div>
                  <div className="font-bold text-lg">Il Globo TV</div>
                  <div className="text-white/80 text-sm mt-1 flex-1">Watch on the big screen at home</div>
                  <div className="mt-3 text-sm font-bold underline">Watch now →</div>
                </a>
              ) : (
                <div className="bg-white/10 rounded-2xl p-6 text-center border border-white/20 flex flex-col">
                  <div className="text-4xl mb-2">📺</div>
                  <div className="font-bold text-lg">Il Globo TV</div>
                  <div className="text-white/80 text-sm mt-1 flex-1">Watch on the big screen at home</div>
                </div>
              )}

              {WATCH_YOUTUBE ? (
                <a
                  href={WATCH_YOUTUBE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/15 hover:bg-white/25 backdrop-blur transition-colors rounded-2xl p-6 text-center border border-white/25 flex flex-col"
                >
                  <div className="text-4xl mb-2">▶️</div>
                  <div className="font-bold text-lg">YouTube</div>
                  <div className="text-white/80 text-sm mt-1 flex-1">Stream live and catch up on demand</div>
                  <div className="mt-3 text-sm font-bold underline">Watch now →</div>
                </a>
              ) : (
                <div className="bg-white/10 rounded-2xl p-6 text-center border border-white/20 flex flex-col">
                  <div className="text-4xl mb-2">▶️</div>
                  <div className="font-bold text-lg">YouTube</div>
                  <div className="text-white/80 text-sm mt-1 flex-1">Stream live and catch up on demand</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Tardio Unleashed / the editorial ─────────────────────────── */}
        <div className="bg-gradient-to-r from-[#0b1c33] to-[#1f3a63] rounded-3xl p-8 md:p-12 text-white shadow-2xl mb-14">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <img
              src={UNLEASHED_LOGO}
              alt="Tardio Unleashed"
              onError={hideOnError}
              className="w-36 h-36 md:w-44 md:h-44 rounded-full shadow-2xl ring-4 ring-white/20 object-cover shrink-0"
            />
            <div className="text-center md:text-left">
              <h2 className="text-3xl md:text-4xl font-extrabold mb-3">Tardio Unleashed</h2>
              <p className="text-white/90 text-lg leading-relaxed">
                Every show opens with <strong>the Editorial</strong> — Tony's unfiltered take on
                the day. No spin, no script, no hedging: the story that matters, called as he
                sees it. It's the segment listeners set their watch by, and the reason the
                conversation carries all the way home.
              </p>
              <p className="mt-4 text-white/70">
                Weekdays at 4pm, right at the top of the show.
              </p>
            </div>
          </div>
        </div>

        {/* ── Why listen ───────────────────────────────────────────────── */}
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">
          <span className="bg-gradient-to-r from-[#009246] via-[#CE2B37] to-[#00247D] bg-clip-text text-transparent">
            Why listen
          </span>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
          {HIGHLIGHTS.map((h) => (
            <div
              key={h.title}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
            >
              <div className="text-3xl mb-3">{h.icon}</div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">{h.title}</h3>
              <p className="text-gray-600 leading-relaxed">{h.text}</p>
            </div>
          ))}
        </div>

        {/* ── Talk to the show ─────────────────────────────────────────── */}
        <div className="bg-[#CE2B37] rounded-3xl p-8 md:p-12 text-white shadow-2xl mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center mb-3">
            Be part of the show
          </h2>
          <p className="text-center text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Drive is built on your voice. Call in while we're live, send a text from the car,
            or write to Tony directly — he reads everything.
          </p>
          <div className={`grid gap-5 ${SMS_NUMBER ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
            <a
              href={`tel:${dial(PHONE_NUMBER)}`}
              className="bg-white/15 hover:bg-white/25 backdrop-blur transition-colors rounded-2xl p-6 text-center border border-white/25"
            >
              <div className="text-4xl mb-2">📞</div>
              <div className="font-bold text-lg mb-1">Call during the show</div>
              <div className="text-2xl font-extrabold tracking-wide">{PHONE_NUMBER}</div>
              <div className="text-white/75 text-sm mt-1">Weekdays 4pm – 6pm</div>
            </a>

            {SMS_NUMBER && (
              <div className="bg-white/15 rounded-2xl p-6 text-center border border-white/25">
                <div className="text-4xl mb-2">💬</div>
                <div className="font-bold text-lg mb-1">Text or WhatsApp</div>
                <a
                  href={`sms:${dial(SMS_NUMBER)}`}
                  className="block text-2xl font-extrabold tracking-wide hover:underline"
                >
                  {SMS_NUMBER}
                </a>
                <a
                  href={`https://wa.me/${digits(SMS_NUMBER)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-sm font-semibold bg-white/20 hover:bg-white/30 transition-colors px-3 py-1 rounded-lg"
                >
                  Open in WhatsApp
                </a>
                <div className="text-white/75 text-sm mt-2">Live during the program</div>
              </div>
            )}

            <a
              href={`mailto:${TONY_EMAIL}`}
              className="bg-white/15 hover:bg-white/25 backdrop-blur transition-colors rounded-2xl p-6 text-center border border-white/25"
            >
              <div className="text-4xl mb-2">✉️</div>
              <div className="font-bold text-lg mb-1">Write to Tony</div>
              <div className="text-lg font-extrabold break-all">{TONY_EMAIL}</div>
              <div className="text-white/75 text-sm mt-1">Story tips and feedback welcome</div>
            </a>
          </div>
        </div>

        {/* ── Weekly lineup ────────────────────────────────────────────── */}
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">
          <span className="bg-gradient-to-r from-[#009246] via-[#CE2B37] to-[#00247D] bg-clip-text text-transparent">
            The week at a glance
          </span>
        </h2>
        <p className="text-center text-gray-600 mb-8">
          Every weekday, 4pm to 6pm — here's what's coming up.
        </p>
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-14 border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="bg-[#0b1c33] text-white">
                  <th className="px-3 py-3 text-left font-bold w-20">Time</th>
                  {DAYS.map((d) => (
                    <th key={d} className="px-3 py-3 text-left font-bold">{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.map(([time, ...cells], i) => {
                  const cross = time === '5:30';
                  return (
                    <tr
                      key={time}
                      className={cross ? 'bg-[#009246]/10' : i % 2 ? 'bg-gray-50' : 'bg-white'}
                    >
                      <td className="px-3 py-3 font-bold text-gray-900 whitespace-nowrap align-top">
                        {time}
                      </td>
                      {cells.map((c, j) => (
                        <td
                          key={j}
                          className={`px-3 py-3 align-top ${
                            cross ? 'font-bold text-[#009246]' : 'text-gray-700'
                          }`}
                        >
                          {c || <span className="text-gray-300">—</span>}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-3 text-xs text-gray-500 bg-gray-50 border-t border-gray-100">
            Segments may change with breaking news and special events.
          </p>
        </div>

        {/* ── About Tony ───────────────────────────────────────────────── */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 mb-14">
          <div className="flex flex-col md:flex-row gap-8 items-center md:items-start">
            <img
              src="/tony-tardio.jpg"
              alt="Tony Tardio"
              onError={hideOnError}
              className="w-44 h-44 rounded-2xl object-cover shadow-lg shrink-0"
            />
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-1">Tony Tardio</h2>
              <p className="text-[#CE2B37] font-semibold mb-4">Host, Drive Australia Wide</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                Tony Tardio is one of the most recognisable voices in Australian radio. For
                decades he read the news on Melbourne's <strong>3AW</strong>, becoming a trusted
                presence for hundreds of thousands of listeners across the city.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                He has also been a familiar voice on <strong>Rete Italia for many years</strong>,
                hosting his long-running Friday program for the Italian-Australian community.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Now he brings both worlds together in <em>Drive Australia Wide</em> — the rigour
                of a newsroom career with the warmth and cultural fluency of someone who has
                spent a lifetime speaking to, and for, Italian Australia.
              </p>
            </div>
          </div>
        </div>

        {/* ── How to listen ────────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-[#00247D] to-[#0047AB] rounded-3xl p-8 md:p-12 text-white shadow-2xl text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4">How to listen</h2>
          <p className="text-xl font-semibold mb-6">Monday to Friday · 4pm – 6pm</p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <span className="bg-white/20 px-5 py-3 rounded-xl font-semibold">📻 On air Australia wide</span>
            <span className="bg-white/20 px-5 py-3 rounded-xl font-semibold">💻 Live audio &amp; video on the site</span>
            <span className="bg-white/20 px-5 py-3 rounded-xl font-semibold">📱 Mobile &amp; smart speaker</span>
          </div>

          {/* Get the free app */}
          <p className="font-semibold text-white/90 mb-3">Take Drive with you — get the free Rete Italia app</p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a
              href={APP_IOS}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/15 hover:bg-white/25 backdrop-blur transition-colors px-6 py-3 rounded-xl font-bold border border-white/30 flex items-center gap-2"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M16.365 1.43c0 1.14-.417 2.2-1.25 3.02-.99.98-2.18 1.55-3.29 1.46-.13-1.1.44-2.25 1.24-3.03.9-.87 2.37-1.51 3.3-1.45zM20.6 17.02c-.55 1.27-.81 1.84-1.52 2.96-.99 1.56-2.39 3.5-4.12 3.51-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.02-3.05-1.77-4.04-3.33C.06 15.8-.24 10.7 1.46 8.03c1.2-1.9 3.1-3.01 4.88-3.01 1.82 0 2.96 1 4.47 1 1.46 0 2.35-1 4.45-1 1.59 0 3.27.86 4.47 2.35-3.93 2.15-3.29 7.76.87 9.65z" />
              </svg>
              App Store
            </a>
            <a
              href={APP_ANDROID}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/15 hover:bg-white/25 backdrop-blur transition-colors px-6 py-3 rounded-xl font-bold border border-white/30 flex items-center gap-2"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M3.6 1.8a1 1 0 00-.5.9v18.6a1 1 0 00.5.9l10-10.2-10-10.2zM16.2 8.4L5.9 2.5l8.4 8.6 1.9-2.7zM19.9 11c.6.4.6 1.6 0 2l-2.4 1.4-2.2-2.4 2.2-2.4L19.9 11zM5.9 21.5l10.3-5.9-1.9-2.7-8.4 8.6z" />
              </svg>
              Google Play
            </a>
          </div>

          <a
            href="/ascoltaci"
            className="inline-block bg-[#CE2B37] hover:bg-[#b0242e] transition-colors px-8 py-4 rounded-xl font-bold text-lg shadow-lg"
          >
            ▶ Listen &amp; watch live now
          </a>

          {(TONY_X || TONY_YOUTUBE) && (
            <div className="mt-10 pt-8 border-t border-white/20">
              <p className="font-semibold text-white/90 mb-4">Follow Tony</p>
              <div className="flex flex-wrap justify-center gap-4">
                {TONY_X && (
                  <a
                    href={TONY_X}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Tony Tardio on X"
                    className="bg-white/15 hover:bg-white/25 backdrop-blur transition-colors px-6 py-3 rounded-xl font-bold border border-white/30 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.66l-5.21-6.82-5.96 6.82H1.68l7.73-8.84L1.25 2.25h6.83l4.71 6.23 5.45-6.23zm-1.16 17.52h1.83L7.01 4.13H5.05l12.03 15.64z" />
                    </svg>
                    X
                  </a>
                )}
                {TONY_YOUTUBE && (
                  <a
                    href={TONY_YOUTUBE}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Tony Tardio on YouTube"
                    className="bg-white/15 hover:bg-white/25 backdrop-blur transition-colors px-6 py-3 rounded-xl font-bold border border-white/30 flex items-center gap-2"
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 00.5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 002.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 002.1-2.1c.5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z" />
                    </svg>
                    YouTube
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Drive;
